import { useUser } from '@context/contexts';
import PropTypes from 'prop-types';
import '@css/PopUp.css';
import '@css/Subscription.css';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import BaseDialog from '@components/BaseDialog';
import SubscriptionStatus from '@components/SubscriptionStatus';
import BillingUnavailable from '@components/BillingUnavailable';
import { useApi } from '@hooks/useApi';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useSubscription } from '@hooks/useSubscription';
import FullPageLoader from '@components/FullPageLoader';

// HTTP status code constants
const HTTP_503_FEATURE_DISABLED = 503;

// Format price from cents to display format
const formatPrice = (amount, currency) => {
    if (!amount) {
        return '$0.00';
    }
    const dollars = (amount / 100).toFixed(2);
    const currencySymbol = currency === 'usd' ? '$' : currency.toUpperCase();
    return `${currencySymbol}${dollars}`;
};

const SetupControls = ({
    isPricesLoading,
    priceOptions,
    selectedPriceId,
    setSelectedPriceId,
    checkoutError,
    isCheckoutLoading,
    handleCheckout,
    embedded,
    onClose,
}) => {
    return (
        <>
            <h3>Включить автопродление</h3>
            <p className="subscription-helper-text">
                Выберите период списания, чтобы сохранить карту - подписка будет продлеваться автоматически.
            </p>

            {isPricesLoading ? (
                <div className="subscription-skeleton">
                    <div className="subscription-skeleton-item tall" />
                    <div className="subscription-skeleton-item" />
                    <div className="subscription-skeleton-item" />
                </div>
            ) : (
                <>
                    <div className="subscription-pricing-container">
                        {priceOptions.map((option) => (
                            <button
                                type="button"
                                key={option.value}
                                className={`price-option ${
                                    selectedPriceId === option.id
                                        ? 'selected'
                                        : ''
                                }`}
                                onClick={() =>
                                    setSelectedPriceId(option.id)
                                }
                            >
                                <div className="price-option-label">
                                    {option.label}
                                </div>
                                <div className="price-option-price">
                                    {option.price}
                                </div>
                                <div className="price-option-period">
                                    {option.period}
                                </div>
                                {option.badge ? (
                                    <div className="price-option-badge">
                                        {option.badge}
                                    </div>
                                ) : null}
                            </button>
                        ))}
                    </div>

                    {checkoutError ? (
                        <div className="subscription-error">
                            <p className="subscription-error-text">
                                {checkoutError}
                            </p>
                        </div>
                    ) : null}

                    <div className="pricing-actions">
                        <button
                            className="btn-purchase"
                            onClick={() =>
                                handleCheckout(selectedPriceId)
                            }
                            disabled={
                                isCheckoutLoading || !selectedPriceId
                            }
                        >
                            {isCheckoutLoading ? (
                                <>
                                    <span className="button-spinner" />{' '}
                                    Обработка...
                                </>
                            ) : (
                                'Включить автопродление'
                            )}
                        </button>
                        {!embedded && (
                            <button
                                className="btn-cancel"
                                onClick={onClose}
                                disabled={isCheckoutLoading}
                            >
                                Отмена
                            </button>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

SetupControls.propTypes = {
    isPricesLoading: PropTypes.bool.isRequired,
    priceOptions: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string,
        id: PropTypes.string,
        label: PropTypes.string,
        price: PropTypes.string,
        period: PropTypes.string,
        badge: PropTypes.string,
    })).isRequired,
    selectedPriceId: PropTypes.string,
    setSelectedPriceId: PropTypes.func.isRequired,
    checkoutError: PropTypes.string,
    isCheckoutLoading: PropTypes.bool.isRequired,
    handleCheckout: PropTypes.func.isRequired,
    embedded: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

const CancelRenewalSection = ({ isCancelLoading, handleCancelRenewal }) => {
    return (
        <div className="subscription-info">
            <p>
                Автопродление включено. Отключение не отменяет уже оплаченный
                период - он остаётся активным до даты окончания.
            </p>
            <div className="pricing-actions">
                <button
                    className="btn-cancel"
                    onClick={handleCancelRenewal}
                    disabled={isCancelLoading}
                >
                    {isCancelLoading ? (
                        <>
                            <span className="button-spinner" />{' '}
                            Отключение...
                        </>
                    ) : (
                        'Отключить автопродление'
                    )}
                </button>
            </div>
        </div>
    );
};

CancelRenewalSection.propTypes = {
    isCancelLoading: PropTypes.bool.isRequired,
    handleCancelRenewal: PropTypes.func.isRequired,
};

const PaymentPopUp = ({ closePopUp, embedded = false }) => {
    const { token } = useUser();
    const api = useApi();
    const { stripeBilling, isLoading: flagsLoading } = useFeatureFlags();
    const { subscription, refetch: refetchSubscription } = useSubscription({ enabled: stripeBilling });
    const [pickedPriceId, setPickedPriceId] = useState(null);
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [isCancelLoading, setIsCancelLoading] = useState(false);
    const [checkoutFailure, setCheckoutFailure] = useState(null);

    const autoRenew = Boolean(subscription?.auto_renew);

    const onClose = () => {
        if (typeof closePopUp === 'function') {
            closePopUp();
        }
    };

    const { data: priceOptions = [], isLoading: isPricesLoading, isError: isPricesError } = useQuery({
        queryKey: ['stripe-prices', token],
        queryFn: async () => {
            const data = await api.get('stripe/prices');
            if (!data) {
                return [];
            }

            const options = [];
            if (data.monthly) {
                options.push({
                    id: data.monthly.id,
                    label: 'Ежемесячно',
                    price: formatPrice(data.monthly.amount, data.monthly.currency),
                    period: '/месяц',
                    value: 'monthly',
                });
            }
            if (data.yearly) {
                options.push({
                    id: data.yearly.id,
                    label: 'Ежегодно',
                    price: formatPrice(data.yearly.amount, data.yearly.currency),
                    period: '/год',
                    value: 'yearly',
                });
            }
            return options;
        },
        enabled: Boolean(token) && stripeBilling && !autoRenew,
        staleTime: 10 * 60 * 1000,
    });

    // The first available price is preselected until the user picks one
    const selectedPriceId = pickedPriceId ?? priceOptions[0]?.id ?? null;
    const checkoutError = checkoutFailure ?? (isPricesError ? 'Ошибка загрузки цен. Попробуйте позже' : null);

    const handleCheckout = async (priceId) => {
        const priceOption = priceOptions.find(opt => opt.id === priceId);
        const subscriptionType = priceOption?.value;

        if (!subscriptionType) {
            setCheckoutFailure('Пожалуйста, выберите тип подписки');
            return;
        }

        setIsCheckoutLoading(true);
        setCheckoutFailure(null);

        try {
            const data = await api.post('pay/stripe/setup', { subscriptionType });

            if (data?.checkoutUrl) {
                globalThis.location.href = data.checkoutUrl;
            } else {
                setCheckoutFailure('Не удалось получить ссылку для настройки оплаты');
            }
        } catch (error) {
            console.info('Checkout error:', error);

            if (error.status === HTTP_503_FEATURE_DISABLED) {
                setCheckoutFailure('Настройка оплаты временно недоступна. Пожалуйста, попробуйте позже');
                return;
            }

            setCheckoutFailure(
                error.message || 'Ошибка при попытке настроить оплату. Попробуйте позже'
            );
        } finally {
            setIsCheckoutLoading(false);
        }
    };

    const handleCancelRenewal = async () => {
        setIsCancelLoading(true);

        try {
            await api.post('pay/stripe/cancel-renewal');
            toast.success('Автопродление отключено');
            refetchSubscription();
        } catch (error) {
            console.info('Cancel renewal error:', error);

            const message = error.status === HTTP_503_FEATURE_DISABLED
                ? 'Управление автопродлением временно недоступно. Пожалуйста, попробуйте позже'
                : error.message || 'Ошибка при попытке отключить автопродление';
            toast.error(message);
        } finally {
            setIsCancelLoading(false);
        }
    };

    const renderBillingUI = () => {
        if (flagsLoading) {
            return <FullPageLoader text="Загрузка..." />;
        }

        if (!stripeBilling) {
            return (
                <>
                    <SubscriptionStatus enabled />
                    <BillingUnavailable onClose={onClose} />
                </>
            );
        }

        return (
            <>
                <SubscriptionStatus enabled={stripeBilling} />

                {autoRenew ? (
                    <CancelRenewalSection
                        isCancelLoading={isCancelLoading}
                        handleCancelRenewal={handleCancelRenewal}
                    />
                ) : (
                    <SetupControls
                        isPricesLoading={isPricesLoading}
                        priceOptions={priceOptions}
                        selectedPriceId={selectedPriceId}
                        setSelectedPriceId={setPickedPriceId}
                        checkoutError={checkoutError}
                        isCheckoutLoading={isCheckoutLoading}
                        handleCheckout={handleCheckout}
                        embedded={embedded}
                        onClose={onClose}
                    />
                )}
            </>
        );
    };

    if (embedded) {
        return (
            <div className="subscription-settings-content">
                {renderBillingUI()}
            </div>
        );
    }

    return (
        <BaseDialog isOpen onClose={onClose} title="Настройки оплаты">
            {renderBillingUI()}
        </BaseDialog>
    );
};

PaymentPopUp.propTypes = {
    closePopUp: PropTypes.func,
    embedded: PropTypes.bool,
};

export default PaymentPopUp;
